/**
 * Contexto global mínimo: sesión, cuenta (`/me`), organización activa, uso del
 * período y el cliente de la API. El resto del estado vive en cada vista.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react'
import { TERMINOS_VERSION, type UsageSummary } from '@eco/shared'
import { Api, EcoApiError, type Me, type OrgRef } from '../lib/api'
import { auth, type AuthSession } from '../lib/auth'
import { marcarOrigenEnviado, origenPendiente } from '../lib/origen'
import { limpiarTerminos, terminosPendientes } from '../lib/terminos'

interface AppState {
  api: Api
  session: AuthSession | null
  /** false hasta saber si hay sesión (evita parpadeo al login). */
  ready: boolean
  me: Me | null
  orgId: string | null
  /** Organización activa (personal o de equipo) con el rol de la persona en ella. */
  org: OrgRef | null
  /**
   * Cambia de organización y abre `path` recargando la página: así ninguna vista
   * queda a medias pidiendo datos de la organización anterior.
   */
  switchOrg: (orgId: string, path?: string) => void
  usage: UsageSummary | null
  refreshUsage: () => Promise<void>
  refreshMe: () => Promise<void>
  accountError: string | null
  /** La cuenta está suspendida desde el panel (solo puede exportar o borrar). */
  suspendida: boolean
}

const Ctx = createContext<AppState | null>(null)
const ORG_KEY = 'eco.org'

export function AppProvider({ children, api: injected }: { children: ReactNode; api?: Api }) {
  const [session, setSession] = useState<AuthSession | null>(auth.current())
  const [ready, setReady] = useState(false)
  const [me, setMe] = useState<Me | null>(null)
  const [usage, setUsage] = useState<UsageSummary | null>(null)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [suspendida, setSuspendida] = useState(false)
  const api = useMemo(() => injected ?? new Api(() => auth.accessToken()), [injected])
  // Una suspensión a mitad de sesión llega como 403 en cualquier llamada, no solo en /me
  useEffect(() => {
    api.onError = (e) => {
      if (e.code === 'account_suspended') setSuspendida(true)
    }
    return () => {
      api.onError = null
    }
  }, [api])

  useEffect(() => {
    const off = auth.subscribe(setSession)
    void auth.ready().then(() => {
      setSession(auth.current())
      setReady(true)
    })
    return off
  }, [])

  // La elegida se recuerda en el navegador; si ya no es miembro, vuelve a la personal
  const [wanted, setWanted] = useState<string | null>(() => {
    // `?org=` (links de correos y de la extensión a una reunión de un equipo) manda
    const fromUrl = new URLSearchParams(location.search).get('org')
    try {
      if (fromUrl) localStorage.setItem(ORG_KEY, fromUrl)
      return fromUrl ?? localStorage.getItem(ORG_KEY)
    } catch {
      return fromUrl
    }
  })
  const org = useMemo(() => {
    if (!me) return null
    return (
      me.orgs.find((o) => o.orgId === wanted) ??
      me.orgs.find((o) => o.tipo === 'personal') ??
      me.orgs[0] ??
      null
    )
  }, [me, wanted])
  const orgId = org?.orgId ?? null
  // El cliente manda X-Org-Id solo si no es la personal (la API la elige por defecto)
  api.orgId = org && org.tipo !== 'personal' ? org.orgId : null
  const switchOrg = useCallback((id: string, path = '/') => {
    try {
      localStorage.setItem(ORG_KEY, id)
    } catch {
      /* sin almacenamiento: vale para esta pestaña */
    }
    setWanted(id)
    location.assign(path)
  }, [])

  const refreshMe = useCallback(async () => {
    try {
      const m = await api.me()
      setMe(m)
      setAccountError(null)
      setSuspendida(false)
      if (terminosPendientes()) {
        if (m.user.terminosVersion === TERMINOS_VERSION) limpiarTerminos()
        else
          void api.patchMe({ aceptarTerminos: true }).then(
            () => limpiarTerminos(),
            () => undefined
          )
      }
      const origen = origenPendiente()
      if (origen)
        void api.registrarOrigen(origen).then(
          () => marcarOrigenEnviado(),
          () => undefined
        )
    } catch (e) {
      // Suspendida desde el panel: pantalla propia (solo exportar o borrar)
      if (e instanceof EcoApiError && e.code === 'account_suspended') setSuspendida(true)
      // Cuenta recién creada: el trigger puede tardar un instante (SDD §8.2)
      setAccountError(e instanceof EcoApiError ? e.message : 'No se pudo cargar tu cuenta.')
    }
  }, [api])

  const refreshUsage = useCallback(async () => {
    if (!orgId) return
    try {
      setUsage(await api.usage(orgId))
    } catch {
      /* el uso es informativo */
    }
  }, [api, orgId])

  useEffect(() => {
    if (session) void refreshMe()
    else {
      setMe(null)
      setUsage(null)
      // Otra persona puede entrar en la misma pestaña
      setSuspendida(false)
    }
  }, [session, refreshMe])

  useEffect(() => {
    void refreshUsage()
  }, [refreshUsage])

  const value = useMemo<AppState>(
    () => ({
      api,
      session,
      ready,
      me,
      orgId,
      org,
      switchOrg,
      usage,
      refreshUsage,
      refreshMe,
      accountError,
      suspendida
    }),
    [
      api,
      session,
      ready,
      me,
      orgId,
      org,
      switchOrg,
      usage,
      refreshUsage,
      refreshMe,
      accountError,
      suspendida
    ]
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useApp(): AppState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useApp fuera de AppProvider')
  return v
}

// ---------------------------------------------------------------------------
// Avisos breves (toasts)
// ---------------------------------------------------------------------------
interface Toast {
  id: number
  text: string
  kind: 'ok' | 'error'
}
const ToastCtx = createContext<(text: string, kind?: Toast['kind']) => void>(() => undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((text: string, kind: Toast['kind'] = 'ok') => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t.slice(-2), { id, text, kind }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500)
  }, [])
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export const useToast = () => useContext(ToastCtx)

/** Mensaje legible para cualquier error de la API. */
export function errorText(e: unknown): string {
  if (e instanceof EcoApiError) return e.message
  if (e instanceof Error) return e.message
  return 'Algo salió mal.'
}
